import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { invoicesApi, clientsApi } from '../services/api';

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalClients: 0,
    totalAmount: 0,
    draftAmount: 0,
    sentAmount: 0,
    paidAmount: 0,
  });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [bulkDownloadOpen, setBulkDownloadOpen] = useState(false);
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [invoicesRes, clientsRes] = await Promise.all([
          invoicesApi.getAll(),
          clientsApi.getAll(),
        ]);

        const invoices = invoicesRes.data;
        
        const draftAmount = invoices.filter(inv => inv.status === 'draft').reduce((sum, inv) => sum + inv.total, 0);
        const sentAmount = invoices.filter(inv => inv.status === 'sent' || inv.status === 'overdue').reduce((sum, inv) => sum + inv.total, 0);
        const paidAmount = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0);
        const totalAmount = draftAmount + sentAmount + paidAmount;

        setStats({
          totalInvoices: invoices.length,
          totalClients: clientsRes.data.length,
          totalAmount,
          draftAmount,
          sentAmount,
          paidAmount,
        });

        setRecentInvoices(invoices.slice(0, 5));
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleBulkDownload = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    setBulkDownloadLoading(true);
    try {
      const response = await invoicesApi.bulkDownload(dateRange.startDate, dateRange.endDate);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoices-${dateRange.startDate}-to-${dateRange.endDate}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Bulk download completed successfully');
      setBulkDownloadOpen(false);
      setDateRange({ startDate: '', endDate: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to download invoices');
    } finally {
      setBulkDownloadLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const StatCard = ({ title, value, icon, color }) => (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: 140,
        bgcolor: color,
        color: 'white',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {icon} &nbsp;  &nbsp;
        <Typography component="h2" variant="h6" gutterBottom>
          {title}
        </Typography>
      </Box>
      <Typography component="p" variant="h4">
        {typeof value === 'number' && title.includes('Amount')
          ? `$${value.toFixed(2)}`
          : value}
      </Typography>
    </Paper>
  );

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Amount"
            value={typeof stats.totalAmount === 'number' ? Number(stats.totalAmount).toFixed(2) : stats.totalAmount}
            icon={<MoneyIcon />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Draft Amount"
            value={typeof stats.draftAmount === 'number' ? Number(stats.draftAmount).toFixed(2) : stats.draftAmount}
            icon={<ReceiptIcon />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Payment Awaiting"
            value={typeof stats.sentAmount === 'number' ? Number(stats.sentAmount).toFixed(2) : stats.sentAmount}
       
            icon={<ReceiptIcon />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Amount Paid"
            value={typeof stats.paidAmount === 'number' ? Number(stats.paidAmount).toFixed(2) : stats.paidAmount}
            icon={<MoneyIcon />}
            color="#d32f2f"
          />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" gutterBottom>
          Recent Invoices
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => setBulkDownloadOpen(true)}
          sx={{ ml: 2 }}
        >
          Bulk Download
        </Button>
      </Box>

      <Grid container spacing={2}>
        {recentInvoices.map((invoice) => (
          <Grid item xs={12} key={invoice._id}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Invoice #{invoice.invoiceNumber}
                </Typography>
                <Typography color="textSecondary">
                  Client: {invoice.client?.name || 'N/A'}
                </Typography>
                <Typography color="textSecondary">
                  Amount: ${invoice.total.toFixed(2)}
                </Typography>
                <Typography color="textSecondary">
                  Status: {invoice.status}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  onClick={() => navigate(`/invoices/${invoice._id}`)}
                >
                  View Details
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {recentInvoices.length === 0 && (
        <Typography color="textSecondary" align="center" sx={{ mt: 4 }}>
          No recent invoices found
        </Typography>
      )}

      {/* Bulk Download Dialog */}
      <Dialog open={bulkDownloadOpen} onClose={() => setBulkDownloadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Download Invoices</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select a date range to download all invoices as a ZIP file.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <TextField
              label="Start Date"
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDownloadOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBulkDownload}
            variant="contained"
            disabled={bulkDownloadLoading}
            startIcon={bulkDownloadLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {bulkDownloadLoading ? 'Downloading...' : 'Download ZIP'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dashboard; 